import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import DataQuery from 'scenes/DataIngester/DataQuery/DataQuery';
import UploadFilesHistory from 'scenes/DataIngester/UploadFilesHistory/UploadFilesHistory';
import Dashboard from 'scenes/Kit/Dashboard/Dashboard';
import DashboardAlternativeMode from 'scenes/Kit/DashboardAlternativeMode/DashboardAlternativeMode';
import Leads from 'scenes/Leads/Leads';
import Quizzes from 'scenes/Quizzes/Quizzes';
import RequirementDetail from 'scenes/Requirements/RequirementDetail/RequirementDetail';
import Requirements from 'scenes/Requirements/Requirements';
import AssignOperationRequest from 'scenes/RequirementsOperation/AssignOperationRequest';
import NewOperationRequest from 'scenes/RequirementsOperation/NewOperationRequest';
import OperationRequestDetail from 'scenes/RequirementsOperation/OperationRequestDetail';
import RequirementsOperation from 'scenes/RequirementsOperation/RequirementsOperation';
import RouteReport from 'scenes/RouteReport/RouteReport';
import NewReportAndMenus from 'scenes/Settings/NewReportAndMenus/NewReportAndMenus';
import StoreVisits from 'scenes/StoreVisits/StoreVisits';
import Stores from 'scenes/Stores/Stores';
import TrainingDetail from 'scenes/Trainings/TrainingDetail/TrainingDetail';
import Trainings from 'scenes/Trainings/Trainings';
import Vans from 'scenes/Vans/Vans';
import Videos from 'scenes/Videos/Videos';
import VisitHistory from 'scenes/VisitHistory/VisitHistory';

import { MenuNode } from 'models/MenuNode';
import { User } from 'models/User';

import { MenuType } from '../config/Config';
import { DEFAULT_REDIRECT_AUTH_ROUTE, USER_TYPES } from '../config/Constants';
import MenuAction from '../redux/actions/menuActions/Actions';
import BukToGeoVictoriaMigration from '../scenes/Buk/BukToGeoVictoriaMigration/BukToGeoVictoriaMigration';
import ContactUs from '../scenes/ContactUs/ContactUs';
import DynamicViewer from '../scenes/DynamicViewer/DynamicViewer';
import GeoVictoriaToBukDateConfiguration from '../scenes/GeoVictoria/GeoVictoriaToBukDateConfiguration/GeoVictoriaToBukDateConfiguration';
import GeoVictoriaToBukMigration from '../scenes/GeoVictoria/GeoVictoriaToBukMigration/GeoVictoriaToBukMigration';
import Home from '../scenes/Home/Home';
import Login from '../scenes/Login/Login';
import NotFound from '../scenes/NotFound/NotFound';
import OurServices from '../scenes/OurServices/OurServices';
import PasswordRecovery from '../scenes/PasswordRecovery/PasswordRecovery';
import PowerBiReport from '../scenes/PowerBiReport/PowerBiReport';
import Companies from '../scenes/Settings/Companies/Companies';
import Contacts from '../scenes/Settings/Contacts/Contacts';
import News from '../scenes/Settings/News/News';
import Roles from '../scenes/Settings/Roles/Roles';
import Services from '../scenes/Settings/Services/Services';
import SuperUsers from '../scenes/Settings/SuperUsers/SuperUsers';
import Users from '../scenes/Settings/Users/Users';
import GuestRoute from './components/GuestRoute';
import PrivateRoute from './components/PrivateRoute';

export default function AppRoutes() {
  const location = useLocation();
  const dispatch = useDispatch();
  const user: User = useSelector((state: any) => state.auth);

  useEffect(() => {
    const pathname = (location.pathname || '').toLowerCase();

    const resolveNodePath = (node: MenuNode): string | null => {
      switch (String(node.menuTypeId)) {
        case MenuType.IFRAME_VIEWER:
          return `/dynamic-viewer/${node.code}`;
        case MenuType.POWER_BI_REPORT:
          return `/pbi-viewer/${node.code}`;
        case MenuType.PDF_VIEWER:
          return `/pdf-viewer/${node.code}`;
        case MenuType.OFFICE_VIEWER:
          return `/office-viewer/${node.code}`;
        case MenuType.URL_REDIRECT:
          return `/download/${node.code}`;
        case MenuType.PARENT_MENU:
          return null;
        case MenuType.STANDARD_MENU:
        default:
          return node.redirectLink || DEFAULT_REDIRECT_AUTH_ROUTE;
      }
    };

    interface MenuMatch {
      chain: string[];
      length: number;
      exact: boolean;
    }

    const findKeyChainForPath = (
      nodes: MenuNode[] = [],
      targetPath: string
    ): string[] | null => {
      // Recolecta los menús que coinciden con la ruta (exacto o por prefijo)
      // y luego elige el mejor: exacto si existe; si no, el prefijo más
      // específico. Así las vistas de detalle (/ruta/:id) resaltan su menú
      // base sin registrarlas.
      const matches: MenuMatch[] = [];

      const walk = (nodeList: MenuNode[] = [], ancestors: string[] = []) => {
        for (const node of nodeList) {
          const nodePath = (resolveNodePath(node) || '').toLowerCase();
          const currentChain = [...ancestors, node.code];

          if (nodePath) {
            const isExact = nodePath === targetPath;
            // Se compara contra `nodePath + '/'` para no confundir rutas que
            // comparten prefijo (p. ej. /requirements vs /requirements-review).
            const isPrefix = targetPath.startsWith(`${nodePath}/`);
            if (isExact || isPrefix) {
              matches.push({
                chain: currentChain,
                length: nodePath.length,
                exact: isExact,
              });
            }
          }

          if (node.children && node.children.length > 0) {
            walk(node.children, currentChain);
          }
        }
      };

      walk(nodes, []);

      if (matches.length === 0) return null;

      const exactMatch = matches.find((m) => m.exact);
      if (exactMatch) return exactMatch.chain;

      const longest = matches.reduce((acc, cur) =>
        cur.length > acc.length ? cur : acc
      );
      return longest.chain;
    };

    // Home path special case
    const isHome = pathname === DEFAULT_REDIRECT_AUTH_ROUTE;
    const chain = isHome
      ? ['HOME__PATH__UNSHIFT']
      : findKeyChainForPath((user?.menus as MenuNode[]) || [], pathname);

    if (chain && chain.length > 0) {
      dispatch(MenuAction.update({ selectedKeys: chain }));
    } else {
      dispatch(MenuAction.remove());
    }
  }, [location, user]);

  return (
    <Routes>
      <Route path="/" element={<Navigate to={DEFAULT_REDIRECT_AUTH_ROUTE} />} />
      <Route path="/home" element={<PrivateRoute outlet={<Home />} />} />
      <Route path="/login" element={<GuestRoute outlet={<Login />} />} />
      <Route
        path="/reset-password/:token"
        element={<GuestRoute outlet={<PasswordRecovery />} />}
      />
      <Route
        path="/dynamic-viewer/:key"
        element={
          <PrivateRoute
            outlet={<DynamicViewer />}
            type={MenuType.IFRAME_VIEWER}
          />
        }
      />

      <Route
        path="/pdf-viewer/:key"
        element={
          <PrivateRoute outlet={<DynamicViewer />} type={MenuType.PDF_VIEWER} />
        }
      />

      <Route
        path="/office-viewer/:key"
        element={
          <PrivateRoute
            outlet={<DynamicViewer />}
            type={MenuType.OFFICE_VIEWER}
          />
        }
      />

      <Route
        path="/pbi-viewer/:key"
        element={
          <PrivateRoute
            outlet={<PowerBiReport />}
            type={MenuType.POWER_BI_REPORT}
          />
        }
      />

      <Route
        path="/geo-victoria/buk-migration"
        element={
          <PrivateRoute
            outlet={<GeoVictoriaToBukMigration />}
            requireMenuAccess
          />
        }
      />
      <Route
        path="/geo-victoria/date-configuration"
        element={
          <PrivateRoute
            outlet={<GeoVictoriaToBukDateConfiguration />}
            requireMenuAccess
          />
        }
      />
      <Route
        path="/buk/geo-victoria-migration"
        element={
          <PrivateRoute
            outlet={<BukToGeoVictoriaMigration />}
            requireMenuAccess
          />
        }
      />

      <Route
        path="/contact-us"
        element={<PrivateRoute outlet={<ContactUs />} requireMenuAccess />}
      />

      <Route
        path="/our-services"
        element={<PrivateRoute outlet={<OurServices />} requireMenuAccess />}
      />

      {/* Settings — SUPER_ADMIN / ADMIN; users, roles, reports-and-menus y news también por menú asignado */}
      <Route
        path="/settings/companies"
        element={
          <PrivateRoute
            outlet={<Companies />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
          />
        }
      />

      <Route
        path="/settings/super-users"
        element={
          <PrivateRoute
            outlet={<SuperUsers />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
          />
        }
      />

      <Route
        path="/settings/users"
        element={
          <PrivateRoute
            outlet={<Users />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
            allowMenuAccess
          />
        }
      />

      <Route
        path="/settings/roles"
        element={
          <PrivateRoute
            outlet={<Roles />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
            allowMenuAccess
          />
        }
      />

      <Route
        path="/settings/reports-and-menus"
        element={
          <PrivateRoute
            outlet={<NewReportAndMenus />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
            allowMenuAccess
          />
        }
      />

      <Route
        path="/settings/news"
        element={
          <PrivateRoute
            outlet={<News />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
            allowMenuAccess
          />
        }
      />

      <Route
        path="/settings/contacts"
        element={
          <PrivateRoute
            outlet={<Contacts />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
          />
        }
      />

      <Route
        path="/settings/services"
        element={
          <PrivateRoute
            outlet={<Services />}
            userTypesRequired={[USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN]}
          />
        }
      />

      <Route
        path="/data-ingester/upload-files-history"
        element={
          <PrivateRoute outlet={<UploadFilesHistory />} requireMenuAccess />
        }
      />

      <Route
        path="/data-ingester/data-query"
        element={<PrivateRoute outlet={<DataQuery />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/leads"
        element={<PrivateRoute outlet={<Leads />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/trainings"
        element={<PrivateRoute outlet={<Trainings />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/trainings/:id"
        element={<PrivateRoute outlet={<TrainingDetail />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/quizzes"
        element={<PrivateRoute outlet={<Quizzes />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/videos"
        element={<PrivateRoute outlet={<Videos />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/stores"
        element={<PrivateRoute outlet={<Stores />} requireMenuAccess />}
      />

      <Route
        path="/tienda-perfecta/operational-team"
        element={<PrivateRoute outlet={<Vans />} requireMenuAccess />}
      />

      <Route
        path="/tienda-perfecta/route-report"
        element={<PrivateRoute outlet={<RouteReport />} requireMenuAccess />}
      />

      <Route
        path="/tienda-perfecta/visit-history"
        element={<PrivateRoute outlet={<VisitHistory />} requireMenuAccess />}
      />

      <Route
        path="/tienda-perfecta/store-visits"
        element={<PrivateRoute outlet={<StoreVisits />} requireMenuAccess />}
      />

      <Route
        path="/portal-go/requirements"
        element={
          <PrivateRoute
            outlet={
              <Requirements canCreate basePath="/portal-go/requirements" />
            }
            requireMenuAccess
          />
        }
      />

      <Route
        path="/portal-go/requirements/:id"
        element={
          <PrivateRoute
            outlet={<RequirementDetail mode="manage" />}
            requireMenuAccess
          />
        }
      />

      <Route
        path="/portal-go/requirements-review"
        element={
          <PrivateRoute outlet={<RequirementsOperation />} requireMenuAccess />
        }
      />

      <Route
        path="/portal-go/requirements-review/new"
        element={
          <PrivateRoute outlet={<NewOperationRequest />} requireMenuAccess />
        }
      />

      <Route
        path="/portal-go/requirements-review/:id/assign"
        element={
          <PrivateRoute outlet={<AssignOperationRequest />} requireMenuAccess />
        }
      />

      <Route
        path="/portal-go/requirements-review/:id"
        element={
          <PrivateRoute outlet={<OperationRequestDetail />} requireMenuAccess />
        }
      />

      <Route
        path="/kit/dashboard"
        element={<PrivateRoute outlet={<Dashboard />} requireMenuAccess />}
      />

      <Route
        path="/kit/dashboard-am"
        element={
          <PrivateRoute
            outlet={<DashboardAlternativeMode />}
            requireMenuAccess
          />
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
